// src/components/AnalyticsRA/SendReportModal.jsx
import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

const SendReportModal = ({ 
  show, 
  onClose, 
  selectedProjects, 
  criteriaData,
  criteriaColumns,
  filteredData, 
  totalAssessments,
  avgScore,
  dateFrom,
  dateTo,
  onSendReport
}) => {
  const [users, setUsers] = useState([]);
  const [userEmails, setUserEmails] = useState([]); // [{ userId, email, type: 'main' | 'cc' }] - пользователи из списка
  const [loading, setLoading] = useState(false);
  const [newManualEmail, setNewManualEmail] = useState('');
  const [manualEmails, setManualEmails] = useState([]); // Ручные email: [{ email, type: 'main' | 'cc' }]
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Загрузка данных только при первом открытии модального окна
  useEffect(() => {
    if (show && !hasLoaded) {
      const fetchUsers = async () => {
        setLoading(true);
        try {
          const response = await api.get('/users/admin', {
            params: {
              projects: selectedProjects.length > 0 ? selectedProjects.join(',') : '',
            },
          });
          console.log(response);
          if (response.data && response.data.users) {
            setUsers(response.data.users);
          }
          // Получаем email текущего пользователя из auth
          const authData = JSON.parse(localStorage.getItem('auth'));
          if (authData && authData.user && authData.user.email) {
            setCurrentUserEmail(authData.user.email);
          }
          // Устанавливаем флаг, что данные загружены
          setHasLoaded(true);
        } catch (error) {
          console.error('Ошибка загрузки пользователей:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchUsers();
    }
  }, [show, hasLoaded, selectedProjects]);

  // Сброс при закрытии модального окна
  useEffect(() => {
    if (!show) {
      setUserEmails([]);
      setManualEmails([]);
      setNewManualEmail('');
      setConfirmMessage('');
      setHasLoaded(false); // Сбрасываем флаг для следующего открытия
    }
  }, [show]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Добавление пользователя (галочка) - добавляет в основные или убирает
  const handleUserToggle = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user || !user.email) return;
    
    if (userEmails.some(u => u.userId === userId)) {
      // Убираем пользователя из основных и копий
      setUserEmails(prev => prev.filter(u => u.userId !== userId));
    } else {
      // Добавляем пользователя в основные
      setUserEmails(prev => [...prev, { userId: user.id, email: user.email, type: 'main' }]);
    }
  };

  // Переключение типа пользователя (main <-> cc)
  const toggleUserType = (userId) => {
    setUserEmails(prev => prev.map(u => 
      u.userId === userId 
        ? { ...u, type: u.type === 'main' ? 'cc' : 'main' }
        : u
    ));
  };

  // Удаление пользователя
  const removeUser = (userId) => {
    setUserEmails(prev => prev.filter(u => u.userId !== userId));
  };

  // Добавление ручных email (только в основные по умолчанию)
  const addManualEmails = () => {
    if (!newManualEmail.trim()) return;
    
    // Разбиваем по запятым и фильтруем пустые значения
    const emails = newManualEmail
      .split(',')
      .map(e => e.trim())
      .filter(e => e.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    
    if (emails.length > 0) {
      // Добавляем только новые email (не в основных и не в копиях)
      const newEmails = emails.filter(e => 
        !manualEmails.some(m => m.email === e)
      );
      
      // Добавляем как основные по умолчанию
      setManualEmails(prev => [...prev, ...newEmails.map(email => ({ email, type: 'main' }))]);
      setNewManualEmail('');
    }
  };

  // Переключение типа email (main <-> cc)
  const toggleEmailType = (email) => {
    setManualEmails(prev => prev.map(m => 
      m.email === email 
        ? { ...m, type: m.type === 'main' ? 'cc' : 'main' }
        : m
    ));
  };

  // Удаление email
  const removeManualEmail = (email) => {
    setManualEmails(prev => prev.filter(m => m.email !== email));
  };

  const handleManualEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addManualEmails();
    }
  };

  const handleSendReport = async () => {
    // Если передан кастомный обработчик отправки, используем его
    if (onSendReport) {
      onSendReport();
      return;
    }
    
    // Получаем email из выбранных пользователей (основные)
    const mainUserEmails = userEmails
      .filter(u => u.type === 'main')
      .map(u => u.email);
    
    // Получаем email из копий (пользователи)
    const ccUserEmails = userEmails
      .filter(u => u.type === 'cc')
      .map(u => u.email);
    
    // Получаем email из ручных (основные и копии)
    const mainManualEmails = manualEmails
      .filter(m => m.type === 'main')
      .map(m => m.email);
    
    const ccManualEmails = manualEmails
      .filter(m => m.type === 'cc')
      .map(m => m.email);
    
    // Собираем все копии
    const allCcEmails = [...ccUserEmails, ...ccManualEmails];
    
    // Собираем все основные (пользователи + ручные)
    const allMainEmails = [...mainUserEmails, ...mainManualEmails];
    
    // Проверяем, что есть хотя бы один получатель
    if (allMainEmails.length === 0 && allCcEmails.length === 0) {
      window.showToast?.('Добавьте хотя бы одного получателя в основные или копии');
      return;
    }
    
    // Данные для отправки
    const reportData = {
      recipients: allMainEmails,
      cc_recipients: allCcEmails,
      projects: selectedProjects,
      // criteriaData: criteriaData,
      // criteriaColumns: criteriaColumns,
      // filteredData: filteredData,
      totalAssessments: totalAssessments,
      avgScore: avgScore,
      currentUserEmail: currentUserEmail,
      dateFrom: dateFrom,
      dateTo: dateTo
    };
    
    console.log('Данные для отправки отчета:', reportData);
    
    // Отправка отчета через API
    try {
      const response = await api.post('/quality/assessment-selection/send-report', reportData);
      console.log('Ответ от сервера:', response.data);
      onClose();
      // window.showToast?.('Отчет отправлен успешно!');
    } catch (error) {
      console.error('Ошибка при отправке отчета:', error);
      // window.showToast?.('Ошибка при отправке отчета');
    }
  };

  // Мемоизация контента модального окна для оптимизации
  const modalContent = useMemo(() => {
    if (!show) return null;

    return (
      <div className="modal-overlay" onClick={handleBackdropClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px'
          }}>
            <h3>Отправить отчет на почту</h3>
            <button 
              type="button" 
              className="text-modal-close" 
              onClick={onClose}
              style={{
                fontSize: '24px',
                lineHeight: '1',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                color: '#6c757d'
              }}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            {loading && (
              <div className="alert alert-info">Загрузка списка сотрудников...</div>
            )}
            
            {/* Список сотрудников */}
            <div className="form-group">
              <label className="form-label">Выберите сотрудников:</label>
              <div className="agent-checkboxes-container" style={{ 
                maxHeight: '200px', 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '10px', 
                backgroundColor: '#f9f9f9' 
              }}>
                {users.length === 0 ? (
                  <p className="text-muted">{loading ? 'Загрузка...' : 'Нет доступных сотрудников'}</p>
                ) : (
                  users.map(user => (
                    <div key={user.id} className="form-check form-check-inline" style={{ minWidth: '250px', marginBottom: '8px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={userEmails.some(u => u.userId === user.id)}
                        onChange={() => handleUserToggle(user.id)}
                      />
                      <label className="form-check-label" htmlFor={`user-${user.id}`} style={{ fontSize: '14px' }}>
                        {user.name || user.username} {user.surname || ''} 
                        {user.email && ` (${user.email})`}
                      </label>
                    </div>
                  ))
                )}
              </div>
              <small className="text-muted">
                Выбрано сотрудников: {userEmails.length} из {users.length}
              </small>
            </div>
            
            {/* Ввод дополнительных email */}
            <div className="form-group">
              <label className="form-label">Добавить дополнительные email (через запятую):</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newManualEmail}
                  onChange={(e) => setNewManualEmail(e.target.value)}
                  onKeyDown={handleManualEmailKeyDown}
                  placeholder="example@mail.com, another@example.com"
                  disabled={loading}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={addManualEmails}
                  disabled={loading || !newManualEmail.trim()}
                >
                  Добавить (в основные)
                </button>
              </div>
            </div>
            
            {/* Список основных получателей (To) */}
            <div className="form-group">
              <label className="form-label" style={{ color: '#198754', fontWeight: 'bold' }}>
                Основные получатели (To):
              </label>
              {userEmails.filter(u => u.type === 'main').length === 0 && manualEmails.filter(m => m.type === 'main').length === 0 ? (
                <p className="text-muted">Нет основных получателей</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {/* Пользователи из списка (только основные) */}
                  {userEmails
                    .filter(u => u.type === 'main')
                    .map(({ userId, email }) => (
                      <span 
                        key={`main-user-${userId}`}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#d1e7dd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleUserType(userId)}
                        title="Нажмите, чтобы переместить в копии"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUserType(userId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#ffc107'
                          }}
                          title="Переместить в копии"
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeUser(userId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#dc3545'
                          }}
                          title="Удалить"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  
                  {/* Ручные email */}
                  {manualEmails
                    .filter(m => m.type === 'main')
                    .map(({ email }) => (
                      <span 
                        key={`main-manual-${email}`}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#d1e7dd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleEmailType(email)}
                        title="Нажмите, чтобы переместить в копии"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEmailType(email);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#ffc107'
                          }}
                          title="Переместить в копии"
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeManualEmail(email);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#dc3545'
                          }}
                          title="Удалить"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>
            
            {/* Список копий (Cc) */}
            <div className="form-group">
              <label className="form-label" style={{ color: '#0d6efd', fontWeight: 'bold' }}>
                Копии (Cc):
              </label>
              {userEmails.filter(u => u.type === 'cc').length === 0 && manualEmails.filter(m => m.type === 'cc').length === 0 ? (
                <p className="text-muted">Нет копий</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {/* Пользователи в копиях */}
                  {userEmails
                    .filter(u => u.type === 'cc')
                    .map(({ userId, email }) => (
                      <span 
                        key={`cc-user-${userId}`}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#cfe2ff',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleUserType(userId)}
                        title="Нажмите, чтобы переместить в основные"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUserType(userId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#198754'
                          }}
                          title="Переместить в основные"
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeUser(userId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#dc3545'
                          }}
                          title="Удалить"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  
                  {/* Ручные email в копиях */}
                  {manualEmails
                    .filter(m => m.type === 'cc')
                    .map(({ email }) => (
                      <span 
                        key={`cc-manual-${email}`}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#cfe2ff',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleEmailType(email)}
                        title="Нажмите, чтобы переместить в основные"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEmailType(email);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#198754'
                          }}
                          title="Переместить в основные"
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeManualEmail(email);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            lineHeight: '1',
                            color: '#dc3545'
                          }}
                          title="Удалить"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              )}
              <small className="text-muted">
                Всего получателей: {userEmails.filter(u => u.type === 'main').length + manualEmails.filter(m => m.type === 'main').length} основных + {userEmails.filter(u => u.type === 'cc').length + manualEmails.filter(m => m.type === 'cc').length} копий
              </small>
            </div>
          </div>
          
          <div className="modal-footer" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '20px'
          }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSendReport}
              disabled={userEmails.filter(u => u.type === 'main').length === 0 && manualEmails.filter(m => m.type === 'main').length === 0}
            >
              Отправить отчет
            </button>
          </div>
        </div>
      </div>
    );
  }, [show, loading, users, userEmails, manualEmails, newManualEmail, handleSendReport, onClose]);

  return modalContent;
};

export default SendReportModal;
